import { format, startOfDay, subDays, subWeeks, subMonths, startOfWeek, startOfMonth } from "date-fns";
import useTranslation from "../client/hooks/useTranslation";

type RecurrenceType = "daily" | "weekly" | "monthly";

interface GoalPeriodHistoryEntry {
  id: string;
  periodStart: Date | string;
  periodEnd: Date | string;
  targetValue: number;
  finalValue: number;
  wasAchieved: boolean;
}

interface GoalHeatmapProps {
  history: GoalPeriodHistoryEntry[];
  recurrence: RecurrenceType;
  targetValue: number;
}

function getPeriodKey(date: Date, recurrence: RecurrenceType): string {
  if (recurrence === "daily") return format(date, "yyyy-MM-dd");
  if (recurrence === "weekly") {
    const monday = startOfWeek(date, { weekStartsOn: 1 });
    return format(monday, "yyyy-MM-dd");
  }
  return format(startOfMonth(date), "yyyy-MM");
}

export default function GoalHeatmap({ history, recurrence, targetValue }: GoalHeatmapProps) {
  const t = useTranslation();

  const periodCount = recurrence === "daily" ? 30 : 12;

  const historyMap = new Map<string, GoalPeriodHistoryEntry>();
  for (const entry of history) {
    const key = getPeriodKey(new Date(entry.periodEnd), recurrence);
    historyMap.set(key, entry);
  }

  const periods: { key: string; label: string; entry: GoalPeriodHistoryEntry | null }[] = [];
  const now = new Date();

  for (let i = periodCount - 1; i >= 0; i--) {
    let periodDate: Date;
    let label: string;

    if (recurrence === "daily") {
      periodDate = subDays(startOfDay(now), i);
      label = format(periodDate, "dd/MM");
    } else if (recurrence === "weekly") {
      periodDate = subWeeks(startOfWeek(now, { weekStartsOn: 1 }), i);
      label = format(periodDate, "dd/MM");
    } else {
      periodDate = subMonths(startOfMonth(now), i);
      label = format(periodDate, "MMM yy");
    }

    const key = getPeriodKey(periodDate, recurrence);
    periods.push({ key, label, entry: historyMap.get(key) ?? null });
  }

  const achievedCount = periods.filter((p) => p.entry?.wasAchieved).length;
  const dataCount = periods.filter((p) => p.entry !== null).length;

  const unitLabel =
    recurrence === "daily"
      ? t.goals.heatmapDays
      : recurrence === "weekly"
      ? t.goals.heatmapWeeks
      : t.goals.heatmapMonths;

  if (history.length === 0) {
    return (
      <p className="text-xs text-muted-foreground mt-2">{t.goals.heatmapNoData}</p>
    );
  }

  return (
    <div className="mt-3 space-y-2">
      <div className="flex items-center gap-1 flex-wrap">
        {periods.map((p) => {
          let bg = "bg-muted";
          let title = p.label;

          if (p.entry) {
            bg = p.entry.wasAchieved ? "bg-pnlPositive" : "bg-pnlNegative";
            title = `${p.label} — ${p.entry.finalValue.toFixed(2)} ${t.goals.heatmapOf} ${p.entry.targetValue.toFixed(2)}`;
          }

          return (
            <div
              key={p.key}
              className={`w-5 h-5 rounded-sm ${bg} cursor-default transition-opacity hover:opacity-80`}
              title={title}
            />
          );
        })}
      </div>
      <p className="text-xs text-muted-foreground">
        {achievedCount} {t.goals.heatmapOf} {dataCount} {unitLabel} {t.goals.heatmapAchieved}
      </p>
    </div>
  );
}
